import {
    DashboardPageContent,
    FormWrapper,
    PageTitle,
} from 'custom-style-components';
import { EventType, People } from 'types/models';
import {
    Flex,
    useToast,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import ConfirmDeleteComponent from 'components/ConfirmDeleteComponent';
import CustomButton from 'components/Button';
import ErrorMessage from 'components/ErrorMessage';
import EventsTable from 'components/EventTable';
import { GenericFrontError } from 'errors/GenericFrontError';
import LoadingSpinner from 'components/LoadingSpinner';
import { ResponseError } from 'errors/ResponseError';
import ShowParticipatingUsers from 'pages/Dashboard/Organizer/components/ShowParticipatingUsers';
import TOAST from 'constants/TOAST';
import { api } from 'services/axios';
import createCancelTokenSource from 'utils/createCancelTokenSource';
import { fetchManyEvents } from 'services/fetch/events';
import { fetchManyUsersFromEvent } from 'services/fetch/users';
import { useRouteMatch } from 'react-router-dom';

const ListEvents: React.FC = () => {
    const toast = useToast({
        duration: TOAST.DURATION,
        position: TOAST.DEFAULT_POSITION,
        isClosable: TOAST.CLOSABLE_BY_DEFAULT,
    });

    const [eventList, setEventList] = useState<EventType[]>([]);
    const [eventData, setEventData] = useState({} as EventType);
    const [modalOpen, setModalOpen] = useState(false);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();

    const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
    const [idToDelete, setIdToDelete] = useState(-1);

    const { path } = useRouteMatch();
    const source = useRef(createCancelTokenSource());

    const loadEvents = useCallback(() => {
        source.current = createCancelTokenSource();
        setLoading(true);
        fetchManyEvents(source.current.token, {
            limit: Number.MAX_SAFE_INTEGER,
            all: true,
        })
            .then(({ events }) => setEventList(events))
            .catch((err) => {
                setError(err.message);
                toast({ title: err.message, status: 'error' });
            })
            .finally(() => setLoading(false));
    }, [toast]);

    useEffect(() => {
        loadEvents();
        return () => source.current.cancel();
    }, [loadEvents]);

    // Atualiza o estado local imediatamente para feedback instantâneo ao admin,
    // sem precisar recarregar a lista inteira da API a cada toggle.

    const changeVisibleStatus = useCallback(async (id: number, newStatus: boolean) => {
        setEventList((prev) =>
            prev.map((e) => (e.id === id ? { ...e, statusVisible: newStatus } : e))
        );
        try {
            await api.put(`/sge/update_status/${id}`, { statusVisible: newStatus });
            toast({
                title: newStatus ? 'Evento visível' : 'Evento invisível',
                status: 'info',
                duration: 2000,
            });
        } catch (err) {
            // Reverte o estado local em caso de falha na API
            setEventList((prev) =>
                prev.map((e) => (e.id === id ? { ...e, statusVisible: !newStatus } : e))
            );
            toast({
                title: err instanceof GenericFrontError ? err.message : 'Erro inesperado',
                status: 'error',
            });
        }
    }, [toast]);

    const changeActiveStatus = useCallback(async (id: number, newStatus: boolean) => {
        // Reflete a mudança na UI antes da resposta da API
        setEventList((prev) =>
            prev.map((e) => (e.id === id ? { ...e, statusActive: newStatus } : e))
        );
        try {
            await api.put(`/sge/update_status/${id}`, { statusActive: newStatus });
            toast({
                title: newStatus ? 'Evento ativo' : 'Evento inativo',
                status: 'info',
                duration: 2000,
            });
        } catch (err) {
            // Reverte o estado local em caso de falha na API
            setEventList((prev) =>
                prev.map((e) => (e.id === id ? { ...e, statusActive: !newStatus } : e))
            );
            toast({
                title: err instanceof GenericFrontError ? err.message : 'Erro inesperado',
                status: 'error',
            });
        }
    }, [toast]);

    const handleParticipantsClick = useCallback((event: EventType) => {
        setEventData(event);
        setModalOpen(true);
    }, []);

    const handleDeleteClick = useCallback((id: number) => {
        setIdToDelete(id);
        setConfirmDeleteModalOpen(true);
    }, []);

    const deleteEvent = async (id: number) => {
        try {
            await api.delete(`/sge/${id}`);
            toast({
                title: 'Edição excluída',
                description: `Exclusão da edição ${id}`,
                status: 'success',
            });
            loadEvents();
        } catch (err) {
            let message = 'Erro';
            if (err instanceof GenericFrontError) {
                message = err.message;
                if (err instanceof ResponseError && err.status === 404)
                    message = 'Edição não encontrada';
            }
            toast({ title: 'Erro na exclusão', description: message, status: 'error' });
        } finally {
            setConfirmDeleteModalOpen(false);
        }
    };

    return (
        <DashboardPageContent>
            <ShowParticipatingUsers
                event={eventData}
                modalOpen={modalOpen}
                setModalOpen={setModalOpen}
            />
            <FormWrapper>
                <ConfirmDeleteComponent
                    modalOpen={confirmDeleteModalOpen}
                    setModalOpen={setConfirmDeleteModalOpen}
                    handleDelete={() => deleteEvent(idToDelete)}
                />

                <PageTitle>Edições</PageTitle>
                <CustomButton link={'edicoes/cadastrar'}>
                    Cadastrar edição
                </CustomButton>

                {error ? (
                    <ErrorMessage>{error}</ErrorMessage>
                ) : loading ? (
                    <Flex justify="center" p={10}>
                        <LoadingSpinner />
                    </Flex>
                ) : (
                    <EventsTable
                        events={eventList}
                        path={path}
                        onDeleteClick={handleDeleteClick}
                        onParticipantsClick={handleParticipantsClick}
                        onActiveChange={changeActiveStatus}
                        onVisibleChange={changeVisibleStatus}
                    />
                )}
            </FormWrapper>
        </DashboardPageContent>
    );
};

export default ListEvents;
